import React, { useState } from "react";
import styled from "styled-components";
import { Colors } from "../../../colors/Colors";
import checkbox from "./images/checkbox.svg";
import manWithBook from "./images/man_with_book.png";
import { SPEEDY_LOGO } from "../../../constants/speedy";

const INPUT_WIDTH = 328;
const INPUT_WIDTH_MD = 224;
const INPUT_WIDTH_SM = 280;

const INITIAL_VALUES = {
  name: "",
  phone: "",
  email: "",
  productVariant: "",
  delivery: "speedy",
  city: "",
  postalCode: "",
  street: "",
  streetNumber: "",
  apartment: "",
};

const Container = styled.div`
  height: calc(100vh - 125px);
  overflow: hidden;
  overflow-y: scroll;
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 40% 60%;
  align-items: start;
  max-width: 1200px;
  margin: 0 auto;
  z-index: 2;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column-reverse;
  }
`;

const BackgroundDiv = styled.div`
  position: fixed;
  overflow: hidden;
  height: 230vw;
  width: 500vw;
  left: calc(100% - 50vw);
  top: 50%;
  transform: translateY(-50%);
  z-index: -2;
  background: transparent;

  &::after {
    display: block;
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: var(--light-main-color);
    border-radius: 60%;
    z-index: 0;
  }
`;

const inputStyles = `
  border: none;
  border-radius: 5px;
  padding: 10px 0;
  width: 100%;
  font-size: 18px;
  text-align: center;
  color: var(--main-color);
  background: var(--white-color);
  font-family: inherit;

  &::placeholder {
    color: var(--main-color);
  }

  &:focus {
    outline: 2px solid var(--main-color);
  }

  @media (max-width: 900px) {
    background-color: var(--lighter-main-color);
  }
`;

const StyledInput = styled.input`
  ${inputStyles}
`;

const StyledSelect = styled.select`
  ${inputStyles}
  appearance: none;
  cursor: pointer;
  text-align-last: center;
  padding-right: 32px;
  padding-left: 32px;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%231E2D6A' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:invalid {
    color: var(--main-color);
  }
`;

const Field = styled.div`
  width: ${INPUT_WIDTH}px;

  @media (max-width: 1100px) {
    width: ${INPUT_WIDTH_MD}px;
  }

  @media (max-width: 900px) {
    width: ${INPUT_WIDTH_SM}px;
  }
`;

const FieldRow = styled(Field)`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || "1fr 1fr"};
  gap: 10px;
`;

const FieldsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const HeadingWrapper = styled.div`
  top: 80px;
  position: sticky;
  padding-top: 40px;

  @media (max-width: 900px) {
    position: static;
  }
`;

const Heading = styled.h3`
  font-weight: 400 !important;
  text-align: left;
  margin-bottom: 20px;
  color: var(--main-color);
  font-size: 47px !important;

  @media (max-width: 900px) {
    font-size: 40px !important;
  }
`;

const HeadingBold = styled(Heading)`
  font-weight: 900 !important;
`;

const FormHeading = styled.h3`
  font-weight: 800 !important;
  margin-bottom: 28px;
  color: #fff;
  font-size: 20px !important;
  text-align: center;

  @media (max-width: 900px) {
    color: var(--main-color);
  }
`;

const SectionTitle = styled.p`
  width: 100%;
  color: white;
  font-weight: 800;
  font-size: 16px;
  text-align: center;
  margin: 12px 0 -8px 0;

  @media (max-width: 900px) {
    color: var(--main-color);
  }
`;

const DeliveryOption = styled.label`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 12px;
  row-gap: 6px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--white-color);
  border: 2px solid
    ${({ selected }) => (selected ? "var(--main-color)" : "transparent")};
  color: var(--main-color);
  cursor: pointer;
  box-sizing: border-box;

  input {
    accent-color: var(--main-color);
    width: 18px;
    height: 18px;
    margin: 0;
  }

  @media (max-width: 900px) {
    background-color: var(--lighter-main-color);
  }
`;

const DeliveryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 10px;

  strong {
    font-size: 15px;
  }
`;

const DeliveryInfo = styled.span`
  grid-column: 2;
  font-size: 12px;
  text-align: left;
  opacity: 0.8;
`;

const SpeedyLogo = styled.img`
  height: 34px;
`;

const PriceBox = styled(Field)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 28px auto 0;
  padding: 12px 18px;
  border-radius: 10px;
  box-sizing: border-box;
  background: var(--main-color);
  color: white;

  span {
    font-size: 16px;
  }

  strong {
    font-size: 28px;
    font-weight: 900;
  }
`;

const SubmitButton = styled.button`
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${INPUT_WIDTH}px;
  gap: 5px;
  padding: 12px 38px;
  border-radius: 9999px;
  font-size: 3rem;
  color: white;
  background-color: var(--main-color);
  font-weight: 700;
  margin: 28px 0;
  cursor: pointer;

  @media (max-width: 1100px) {
    font-size: 18px;
    padding: 10px 20px;
    width: ${INPUT_WIDTH_MD}px;
  }

  @media (max-width: 900px) {
    width: ${INPUT_WIDTH_SM}px;
  }
`;

const Form = styled.form`
  justify-content: center;
  align-items: center;
  max-width: 430px;
  justify-self: flex-end;
  padding: 40px 20px 20px;
  background: transparent;

  @media (max-width: 1100px) {
    max-width: 320px;
  }
`;

const SideWrapper = styled.div`
  min-height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: transparent;

  @media (max-width: 900px) {
    max-height: fit-content;
    min-height: calc(40vh + 200px);
  }
`;

const ManWithBookWrapper = styled.div`
  position: absolute;
  z-index: -1;
  height: 500px;
  bottom: 0;
  right: 70%;

  @media (max-width: 900px) {
    position: static;
    height: 400px;
  }
`;

const ManImage = styled.img`
  height: 100%;
  max-height: 60vh;
  position: absolute;
  bottom: 0;

  @media (max-width: 900px) {
    height: fit-content;
    max-height: fit-content;
    position: static;
  }
`;

const RulesWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const Rules = styled.p`
  color: white;
  margin-bottom: 14px;
  font-size: 14px !important;

  @media (max-width: 900px) {
    color: ${Colors.mainColor};
  }
`;

const RulesLink = styled.a`
  color: white;
  text-decoration: underline;

  @media (max-width: 900px) {
    color: ${Colors.mainColor};
  }
`;

const CheckBox = styled.input`
  display: none;
`;

const CustomCheckBox = styled.p`
  width: 21px;
  height: 21px;
  border-radius: 6px;
  flex-shrink: 0;
  cursor: pointer;
  border: 1px solid white;

  @media (max-width: 900px) {
    border: 1px solid var(--main-color);
  }
`;

const CheckedCustomCheckBox = styled.img`
  width: 21px;
  height: 21px;
  flex-shrink: 0;
  border: 1px solid var(--main-color);
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
`;

const MoreTextWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 10px;
  color: white;

  @media (max-width: 900px) {
    color: ${Colors.mainColor};
  }
`;

const MoreTextButton = styled.button`
  color: white;
  border: none;
  background: none;
  font-size: 10px;
  cursor: pointer;

  @media (max-width: 900px) {
    color: ${Colors.mainColor};
  }
`;

const ThanksWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  color: ${Colors.mainColor};

  h3 {
    text-align: center;
  }
`;

const FormSenderV2 = ({ data }) => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [checkedA, setCheckedA] = useState(false);
  const [checkedB, setCheckedB] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showButtonThx, setShowButtonThx] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const submitForm = (e) => {
    e.preventDefault();

    // Intentionally no request - the form only resets after a valid submit.
    e.target.reset();
    setValues(INITIAL_VALUES);
    setCheckedA(false);
    setCheckedB(false);
    setShowMore(false);
    setShowButtonThx(false);

    setTimeout(() => {
      setShowButtonThx(true);
    }, 6000);
  };

  return (
    <>
      <BackgroundDiv />
      <Container>
        <Wrapper>
          <SideWrapper>
            <HeadingWrapper>
              <HeadingBold>{data.title}</HeadingBold>
              <Heading>{data.title1}</Heading>
            </HeadingWrapper>
            <ManWithBookWrapper>
              <ManImage alt="man with book" src={manWithBook} />
            </ManWithBookWrapper>
          </SideWrapper>
          <Form onSubmit={submitForm}>
            <FormHeading>{data.subtitle}</FormHeading>
            <FieldsColumn>
              <Field>
                <StyledSelect
                  name="productVariant"
                  value={values.productVariant}
                  onChange={handleChange}
                  aria-label={data.languageLabel}
                  required
                >
                  <option value="" disabled>
                    {data.languageLabel}
                  </option>
                  {data.productVariants.map((variant) => (
                    <option key={variant.value} value={variant.value}>
                      {variant.label}
                    </option>
                  ))}
                </StyledSelect>
              </Field>
              <Field>
                <StyledInput
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  placeholder={data.button1}
                  autoComplete="name"
                  type="text"
                  required
                />
              </Field>
              <Field>
                <StyledInput
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  placeholder={data.button2}
                  autoComplete="tel"
                  type="tel"
                  pattern="^(\+359|0)[\s\-]?[0-9][0-9\s\-]{7,11}$"
                  title={data.phoneHint}
                  required
                />
              </Field>
              <Field>
                <StyledInput
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  placeholder={data.button3}
                  autoComplete="email"
                  type="email"
                />
              </Field>

              <SectionTitle>{data.addressTitle}</SectionTitle>
              <FieldRow columns="2fr 1fr">
                <StyledInput
                  name="city"
                  value={values.city}
                  onChange={handleChange}
                  placeholder={data.city}
                  autoComplete="address-level2"
                  type="text"
                  required
                />
                <StyledInput
                  name="postalCode"
                  value={values.postalCode}
                  onChange={handleChange}
                  placeholder={data.postalCode}
                  autoComplete="postal-code"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength={4}
                  title={data.postalCodeHint}
                  type="text"
                  required
                />
              </FieldRow>
              <FieldRow columns="2fr 1fr">
                <StyledInput
                  name="street"
                  value={values.street}
                  onChange={handleChange}
                  placeholder={data.street}
                  autoComplete="address-line1"
                  type="text"
                  required
                />
                <StyledInput
                  name="streetNumber"
                  value={values.streetNumber}
                  onChange={handleChange}
                  placeholder={data.streetNumber}
                  type="text"
                  required
                />
              </FieldRow>
              <Field>
                <StyledInput
                  name="apartment"
                  value={values.apartment}
                  onChange={handleChange}
                  placeholder={data.apartment}
                  autoComplete="address-line2"
                  type="text"
                />
              </Field>

              <SectionTitle>{data.deliveryTitle}</SectionTitle>
              <Field>
                <DeliveryOption selected={values.delivery === "speedy"}>
                  <input
                    type="radio"
                    name="delivery"
                    value="speedy"
                    checked={values.delivery === "speedy"}
                    onChange={handleChange}
                    required
                  />
                  <DeliveryHeader>
                    <strong>{data.deliverySpeedy}</strong>
                    <SpeedyLogo alt="Speedy" src={SPEEDY_LOGO} />
                  </DeliveryHeader>
                  <DeliveryInfo>{data.deliverySpeedyInfo}</DeliveryInfo>
                </DeliveryOption>
              </Field>
            </FieldsColumn>

            <PriceBox>
              <span>{data.priceLabel}</span>
              <strong>
                {data.price} {data.currency}
              </strong>
            </PriceBox>

            <div style={{ display: "flex", justifyContent: "center" }}>
              {showButtonThx ? (
                <SubmitButton type="submit">{data.buttonSub}</SubmitButton>
              ) : (
                <ThanksWrapper>
                  <SubmitButton type="button">{data.buttonTXT}</SubmitButton>
                  <h3>{data.buttonText}</h3>
                </ThanksWrapper>
              )}
            </div>
            <label>
              <RulesWrapper>
                <CheckBox
                  checked={checkedA}
                  onChange={(e) => setCheckedA(e.target.checked)}
                  type="checkbox"
                  required
                />
                {checkedA ? (
                  <CheckedCustomCheckBox alt="checkbox" src={checkbox} />
                ) : (
                  <CustomCheckBox />
                )}
                <Rules>
                  {data.checkOne}{" "}
                  <RulesLink
                    href={data.rulesLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {data.subOne}
                  </RulesLink>{" "}
                  <RulesLink
                    href={data.privacyLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {data.subTwo}
                  </RulesLink>
                </Rules>
              </RulesWrapper>
            </label>
            <label>
              <RulesWrapper>
                <CheckBox
                  checked={checkedB}
                  onChange={(e) => setCheckedB(e.target.checked)}
                  type="checkbox"
                  required
                />
                {checkedB ? (
                  <CheckedCustomCheckBox alt="checkbox" src={checkbox} />
                ) : (
                  <CustomCheckBox />
                )}
                <Rules>{data.checkTwo}</Rules>
              </RulesWrapper>
            </label>
            <MoreTextWrapper>
              <p style={{ padding: "10px", paddingBottom: 0, fontSize: "10px" }}>
                {data.textOne}: {showMore ? data.textTwo : "..."}
              </p>{" "}
              <MoreTextButton
                type="button"
                onClick={() => setShowMore((prev) => !prev)}
              >
                <div style={{ marginTop: "11px" }}>
                  {showMore ? data.buttonLess : data.buttonMore}
                </div>
              </MoreTextButton>
            </MoreTextWrapper>
          </Form>
        </Wrapper>
      </Container>
    </>
  );
};

export default FormSenderV2;
